import { Api } from "src/utils/api";
import { ADDS_SALES_API_BASE_URL } from "./ads-sales.constant";

export interface WidgetData {
  sales?: number;
  spend?: number;
  impressions?: number;
  ctr?: number;
  cvr?: number;
  cpc?: number;
  acos?: number;
  roas?: number;
  orders?: number;
  unitSolds?: number;
  clicks?: number;
  tacos?: number;
  organicRank?: number;
  searchTermAdImpressionRank?: number;
  timeInBudget?: number;
  tosAdImpressionShare?: number;
  totalUnitsSold?: number;
  totalSales?: number;
  netProceeds?: number;
  contribution?: number;
  contributionMargin?: number;
  grossMargin?: number;
  roi?: number;
}

export class AdsSalesApi extends Api {
  constructor() {
    super(ADDS_SALES_API_BASE_URL);
  }

  public getAllMetrics = async (
    date_start: string,
    date_end: string,
    saturation: number,
  ): Promise<WidgetData | WidgetData[]> => {
    return await this.get("ads-sales/all", {
      date_start,
      date_end,
      saturation,
    });
  };

  public getImpressions = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/impressions", { date_start, date_end });
  };

  public getClicks = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/clicks", { date_start, date_end });
  };

  public getOrders = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/orders", { date_start, date_end });
  };

  public getUnitsSold = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/units-sold", { date_start, date_end });
  };

  public getSales = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/sales", { date_start, date_end });
  };

  public getSpend = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/spend", { date_start, date_end });
  };

  public getAcos = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/acos", { date_start, date_end });
  };

  public getRoas = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/roas", { date_start, date_end });
  };

  public getCpc = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/cpc", { date_start, date_end });
  };

  public getCvr = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/cvr", { date_start, date_end });
  };

  public getCtr = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/ctr", { date_start, date_end });
  };

  public getTotalUnitsSold = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get("total/units-sold", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getTotalSales = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get("total/sales", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getNetProceeds = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get("total/net-proceeds", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getContributionsProfit = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get("total/contribution-profit", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getContributionsMargin = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get("total/contribution-margin", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getGrossMargin = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get("total/gross-margin", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getRoi = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get("total/roi", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getAllMetricsDiff = async (
    date_start: string,
    date_end: string,
    compare_date_start: string,
    compare_date_end: string,
    saturation: number = 0,
  ): Promise<WidgetData> => {
    return await this.get("ads-sales/all/diff", {
      date_start,
      date_end,
      saturation,
      compare_date_start,
      compare_date_end,
    });
  };

  public getWidgetsComparePromise = async (
    date_start: string,
    date_end: string,
    compare_date_start: string,
    compare_date_end: string,
  ): Promise<Partial<WidgetData>> => {
    try {
      const diffData = await this.getAllMetricsDiff(
        date_start,
        date_end,
        compare_date_start,
        compare_date_end,
        0,
      ).catch(() => ({}));

      const {
        ad_sales,
        ad_spend,
        ad_clicks,
        ad_impressions,
        ad_units_sold,
        ad_orders,
        acos,
        roas,
        cpc,
        ad_ctr,
        ad_cvr,
        tacos,
        organicRank,
        searchTermAdImpressionRank,
        timeInBudget,
        tosAdImpressionShare,
      } = diffData as any;

      return {
        sales: ad_sales ?? 0,
        spend: ad_spend ?? 0,
        impressions: ad_impressions ?? 0,
        ctr: ad_ctr ?? 0,
        cvr: ad_cvr ?? 0,
        cpc: cpc ?? 0,
        acos: acos ?? 0,
        roas: roas ?? 0,
        orders: ad_orders ?? 0,
        unitSolds: ad_units_sold ?? 0,
        clicks: ad_clicks ?? 0,
        tacos: tacos ?? 0,
        organicRank: organicRank ?? 0,
        searchTermAdImpressionRank: searchTermAdImpressionRank ?? 0,
        timeInBudget: timeInBudget ?? 0,
        tosAdImpressionShare: tosAdImpressionShare ?? 0,
      };
    } catch {
      return {};
    }
  };

  public getWidgetDataPromise = async (
    date_start: string,
    date_end: string,
  ): Promise<Partial<WidgetData>> => {
    const [
      sales,
      spend,
      impressions,
      ctr,
      cvr,
      cpc,
      acos,
      roas,
      orders,
      unitsSold,
      clicks,
      tacos,
      organicRank,
      searchTermAdImpressionRank,
      timeInBudget,
      tosAdImpressionShare,
      totalUnitsSold,
      totalSales,
      contribution,
      contributionMargin,
      grossMargin,
      roi,
    ] = await Promise.all([
      this.getSales(date_start, date_end),
      this.getSpend(date_start, date_end),
      this.getImpressions(date_start, date_end),
      this.getCtr(date_start, date_end),
      this.getCvr(date_start, date_end),
      this.getCpc(date_start, date_end),
      this.getAcos(date_start, date_end),
      this.getRoas(date_start, date_end),
      this.getOrders(date_start, date_end),
      this.getUnitsSold(date_start, date_end),
      this.getClicks(date_start, date_end),
      this.getTacos
        ? (this.getTacos(date_start, date_end) as Promise<number>)
        : Promise.resolve(0),
      this.getOrganicRank
        ? (this.getOrganicRank(date_start, date_end) as Promise<number>)
        : Promise.resolve(0),
      this.getSearchTermAdImpressionRank
        ? (this.getSearchTermAdImpressionRank(
            date_start,
            date_end,
          ) as Promise<number>)
        : Promise.resolve(0),
      this.getTimeInBudget
        ? (this.getTimeInBudget(date_start, date_end) as Promise<number>)
        : Promise.resolve(0),
      this.getTosAdImpressionShare
        ? (this.getTosAdImpressionShare(
            date_start,
            date_end,
          ) as Promise<number>)
        : Promise.resolve(0),
      this.getTotalUnitsSold(date_start, date_end),
      this.getTotalSales(date_start, date_end),
      this.getContributionsProfit(date_start, date_end),
      this.getContributionsMargin(date_start, date_end),
      this.getGrossMargin(date_start, date_end),
      this.getRoi(date_start, date_end),
    ]);

    return {
      sales,
      spend,
      impressions,
      ctr,
      cvr,
      cpc,
      acos,
      roas,
      orders,
      unitSolds: unitsSold,
      clicks,
      tacos,
      organicRank,
      searchTermAdImpressionRank,
      timeInBudget,
      tosAdImpressionShare,
      totalUnitsSold,
      totalSales,
      contribution,
      contributionMargin,
      grossMargin,
      roi,
    };
  };
}
